import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, fmt } from "../client";
import { PageHeader, Panel, Pill, TierBadge, Empty } from "../ui";
import LiveChats from "./LiveChats";

const STATUSES = [["open", "Open"], ["in_progress", "Pending"], ["resolved", "Resolved"]];

const Detail = ({ id, onChanged }) => {
  const [t, setT] = useState(null);
  const [text, setText] = useState("");
  const load = useCallback(() => { api(`/staff/tickets/${id}`).then(setT).catch((e) => toast.error(e.message)); }, [id]);
  useEffect(() => { setT(null); load(); }, [load]);
  const post = async (path, body, msg) => {
    try { await api(`/staff/tickets/${id}/${path}`, { method: "POST", body }); toast.success(msg); load(); onChanged(); return true; }
    catch (e) { toast.error(e.message); return false; }
  };
  if (!t) return <p className="p-6 text-sm text-[var(--s-dim)]">Loading…</p>;
  return (
    <div className="flex h-full flex-col" data-testid="ticket-detail">
      <div className="border-b border-[var(--s-line)] p-5">
        <div className="flex flex-wrap items-center gap-2"><TierBadge tier={t.user_tier} data-testid="ticket-tier" /><Pill>{t.category || "general"}</Pill><Pill tone="warn">{t.priority || "normal"}</Pill></div>
        <h2 className="s-display mt-3 text-xl" data-testid="ticket-subject">{t.subject}</h2>
        <p className="text-xs text-[var(--s-dim)]">{t.user_name || t.opened_by_email} · opened {fmt(t.created_at)}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {STATUSES.map(([s, l]) => (
            <button key={s} onClick={() => post("status", { status: s }, `Marked ${l}`)} data-testid={`ticket-status-${s}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${t.status === s ? "border-[var(--s-accent)] bg-[var(--s-accent)] text-black" : "border-[var(--s-line)] hover:border-[var(--s-accent)]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-5" data-testid="ticket-thread">
        {t.description && <p className="rounded-lg bg-white/[0.03] p-3 text-sm">{t.description}</p>}
        {(t.messages || []).filter((m, i) => !(i === 0 && m.text === t.description)).map((m, i) => {
          const staff = m.from === "staff" || m.user_id !== t.opened_by;
          return (
            <div key={i} className={`max-w-[85%] rounded-lg p-3 text-sm ${staff ? "ml-auto border border-[rgba(242,179,61,0.35)] bg-[rgba(242,179,61,0.12)]" : "bg-white/[0.04]"}`}>
              <p>{m.text}</p>
              <p className="s-mono mt-1 text-[10px] text-[var(--s-dim)]">{staff ? m.staff_name || "Staff" : "Customer"} · {fmt(m.at)}</p>
            </div>
          );
        })}
      </div>
      <form className="border-t border-[var(--s-line)] p-4" onSubmit={async (e) => { e.preventDefault(); if (text.trim() && await post("reply", { text }, "Reply sent")) setText(""); }}>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply…" className="s-input" data-testid="ticket-reply-input" />
        <Button type="submit" className="s-btn mt-2" data-testid="ticket-reply-submit">Send reply</Button>
      </form>
    </div>
  );
};

export default function Tickets() {
  const [filter, setFilter] = useState("open");
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const [mode, setMode] = useState("live");
  const load = useCallback(() => { api(`/staff/tickets?status=${filter}`).then(setRows).catch((e) => toast.error(e.message)); }, [filter]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader kicker="Helpdesk" title="Support tickets">
        <div className="flex flex-wrap gap-2">
        <Tabs value={mode} onValueChange={setMode}><TabsList>
          <TabsTrigger value="live" data-testid="tickets-mode-live">Live chats</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tickets-mode-tickets">Tickets</TabsTrigger>
        </TabsList></Tabs>
        {mode === "tickets" && <Tabs value={filter} onValueChange={(v) => { setFilter(v); setRows(null); }}><TabsList>
          {["open", "pending", "resolved"].map((s) => <TabsTrigger key={s} value={s} className="capitalize" data-testid={`tickets-tab-${s}`}>{s}</TabsTrigger>)}
        </TabsList></Tabs>}
        </div>
      </PageHeader>
      {mode === "live" ? <LiveChats /> : (
      <div className="grid gap-4 lg:grid-cols-[minmax(260px,360px)_1fr]">
        <Panel className="max-h-[70vh] divide-y divide-[var(--s-line)] overflow-y-auto" data-testid="tickets-list">
          {rows === null ? <p className="p-5 text-sm text-[var(--s-dim)]">Loading…</p> : rows.length === 0 ? <Empty>No tickets here.</Empty> : rows.map((t) => (
            <button key={t.id} onClick={() => setSel(t.id)} data-testid={`ticket-row-${t.id}`}
              className={`block w-full px-4 py-3 text-left transition-colors hover:bg-white/[0.03] ${sel === t.id ? "border-l-2 border-[var(--s-accent)] bg-white/[0.03]" : ""}`}>
              <p className="truncate text-sm">{t.subject}</p>
              <p className="s-mono text-[10px] text-[var(--s-dim)]">{t.category} · {t.opened_by_email} · {fmt(t.updated_at)}</p>
            </button>
          ))}
        </Panel>
        <Panel className="min-h-[50vh]">{sel ? <Detail id={sel} onChanged={load} /> : <Empty>Select a ticket to view the thread.</Empty>}</Panel>
      </div>
      )}
    </div>
  );
}
